import { queryClient } from 'lib/react-query';
import type { PackEntry } from 'types/shared';
import queryKeys from '../api/queryKeys';
import type { HypothesisCreativeEntry } from '../types/hypothesis';

export const NO_HYPOTHESIS_TAG = 'No hypothesis';
export const NO_HYPOTHESIS_PACK_TAG = 'No hypothesis pack';

export const packTagForHypothesis = (hypothesisLabel: string): string =>
  hypothesisLabel === NO_HYPOTHESIS_TAG ? NO_HYPOTHESIS_PACK_TAG : hypothesisLabel;

export const buildPackEntry = (tag: string, creatives: string[]): PackEntry => ({
  tag,
  adType: '',
  creatives,
  filterCreativeByName: [],
  visual_touch_ups: true,
});

export const getTestCreativesIdToHypothesis = (): Map<string, string> => {
  const cachedTestCreatives = queryClient
    .getQueryCache()
    .findAll({ queryKey: queryKeys.allTestCreatives })
    .flatMap((query) => query.state.data as HypothesisCreativeEntry[])
    .filter((c): c is HypothesisCreativeEntry => !!c);

  const idToHypothesis = new Map<string, string>();
  for (const creative of cachedTestCreatives) {
    if (!idToHypothesis.has(creative.id)) {
      idToHypothesis.set(creative.id, creative.hypothesis ?? NO_HYPOTHESIS_TAG);
    }
  }
  return idToHypothesis;
};

export const groupIdsByHypothesis = (
  selectedIds: readonly string[],
  idToHypothesis: Map<string, string>,
): Map<string, string[]> => {
  const grouped = new Map<string, string[]>();
  for (const id of selectedIds) {
    const hypothesis = idToHypothesis.get(id) ?? NO_HYPOTHESIS_TAG;
    const bucket = grouped.get(hypothesis);
    if (bucket) bucket.push(id);
    else grouped.set(hypothesis, [id]);
  }
  return grouped;
};

/** Splits ids into packCount groups; earlier packs get +1 when count does not divide evenly. */
export const splitIdsIntoPacks = (
  ids: readonly string[],
  packCount: number,
): string[][] => {
  if (packCount < 1 || packCount > ids.length) return [ids.slice()];

  const packs: string[][] = Array.from({ length: packCount }, () => []);
  let index = 0;
  const baseSize = Math.floor(ids.length / packCount);
  let extraPacks = ids.length % packCount;

  for (let packIdx = 0; packIdx < packCount; packIdx++) {
    const size = baseSize + (extraPacks > 0 ? 1 : 0);
    if (extraPacks > 0) extraPacks--;
    packs[packIdx] = ids.slice(index, index + size);
    index += size;
  }

  return packs;
};

export const defaultSplitPackCount = (creativeCount: number): number =>
  Math.min(2, creativeCount);

export const clampSplitPackCount = (
  value: number,
  creativeCount: number,
): number => Math.min(Math.max(1, value), creativeCount);

export const parseSplitPackCountInput = (
  rawValue: string,
  creativeCount: number,
): number => {
  const parsed = Number.parseInt(rawValue.trim(), 10);
  if (Number.isNaN(parsed)) {
    return defaultSplitPackCount(creativeCount);
  }
  return clampSplitPackCount(parsed, creativeCount);
};

export const isValidSplitPackCountInput = (
  rawValue: string,
  creativeCount: number,
): boolean => {
  const trimmed = rawValue.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return false;
  const parsed = Number.parseInt(trimmed, 10);
  return parsed >= 1 && parsed <= creativeCount;
};
